import ReactNativeBlobUtil from 'react-native-blob-util';

import initApi, { invalidateApiCache } from '@api';
import { getSecureValue, setSecureValue, isAndroid } from '@helpers';
import type { UserInfo } from '@api/domain/types';
import { handleTenantReasonResponse } from '@reducers/helpers/tenantStatus';

// Returns `true` when identity is established (tenant is OK and secure values were
// written), `false` when the tenant-reason early-return short-circuited the flow.
// Callers that care about whether the new tenant is unrestricted use this signal
// — see `reinitializeAppState` exiting RESTRICTED_ROOT_ROUTES on a successful switch.
export const generateTokenInfo = async (t: string | false, deviceCode?: string): Promise<boolean> => {
  const token = t || getSecureValue('token');

  let tokenInfo: UserInfo | undefined;
  try {
    const api = initApi();
    const result = await api.user.userInfo(token);

    // Detect tenant-status reason BEFORE consuming the body. The helper clones
    // internally and short-circuits on 2xx / non-JSON responses, so it's safe
    // to call first and a no-op for the normal 200 + UserInfoDto path.
    if (await handleTenantReasonResponse(result as Response)) {
      return false;
    }

    if (!(result as Response).ok) {
      // Non-2xx without a recognized tenant-reason body (e.g. the 500 + empty
      // body the dev gateway returns to Android for OnHold tenants on /userinfo).
      // Fail loudly rather than running result.json() on an empty body and
      // throwing a SyntaxError.
      throw new Error(`generateTokenInfo: userInfo returned ${(result as Response).status}`);
    }

    tokenInfo = await result.json();
  } catch (e) {
    console.error('[generateTokenInfo] userInfo fetch failed', e);
    throw e instanceof Error ? e : new Error(`generateTokenInfo: ${String(e)}`);
  }

  if (!tokenInfo?.env) {
    // env drives gateway URL selection for every subsequent call; without it the
    // session is unusable, so fail loudly instead of leaving a half-populated state.
    throw new Error('generateTokenInfo: missing env in userInfo response');
  }

  if (deviceCode) {
    setSecureValue('deviceCode', deviceCode);
  }

  setSecureValue('env', tokenInfo.env);
  invalidateApiCache(); // Force API instance recreation with the backend-validated env
  setSecureValue('role', tokenInfo.role || '');
  setSecureValue('name', tokenInfo.name || '');
  setSecureValue('firstName', tokenInfo.firstName || '');
  setSecureValue('lastName', tokenInfo.lastName || '');
  setSecureValue('tid', tokenInfo.tenantId || '');
  setSecureValue('email', tokenInfo.email);
  return true;
};

export const getBlob = async (url: string, id: string): Promise<string> => {
  const dowImg = await fetch(url, { ignoreInterceptor: 'true' } as RequestInit);
  const blob = await dowImg.blob();

  return new Promise((resolve, reject) => {
    const fileReaderInstance = new FileReader();
    fileReaderInstance.readAsDataURL(blob);
    fileReaderInstance.onload = () => {
      const base64data = fileReaderInstance.result as string;
      if (isAndroid) {
        const dirs = ReactNativeBlobUtil.fs.dirs.CacheDir;
        const path = `${dirs}/${id}.png`;
        ReactNativeBlobUtil.fs
          .writeFile(path, base64data.replace('data:application/octet-stream;base64,', ''), 'base64')
          .then(() => {
            const file = `file://${path}`;
            resolve(file);
          })
          .catch((error: Error) => {
            reject(error);
          });
      } else {
        resolve(base64data);
      }
    };
    fileReaderInstance.onerror = (error) => {
      reject(error);
    };
  });
};
